import { useState } from "react";
import { useLang } from "../context/LanguageContext";
import { useForm } from "../context/FormContext";

interface Props {
  onClose: () => void;
  fieldTag?: string;
}

interface FaqItem {
  q: string;
  a: string;
}

const FAQ_BY_STEP: Record<number, FaqItem[]> = {
  1: [
    { q: "faq.s1.q1", a: "faq.s1.a1" },
    { q: "faq.s1.q2", a: "faq.s1.a2" },
    { q: "faq.s1.q3", a: "faq.s1.a3" },
  ],
  2: [
    { q: "faq.s2.q1", a: "faq.s2.a1" },
    { q: "faq.s2.q2", a: "faq.s2.a2" },
    { q: "faq.s2.q3", a: "faq.s2.a3" },
  ],
  3: [
    { q: "faq.s3.q1", a: "faq.s3.a1" },
    { q: "faq.s3.q2", a: "faq.s3.a2" },
  ],
  4: [
    { q: "faq.s4.q1", a: "faq.s4.a1" },
    { q: "faq.s4.q2", a: "faq.s4.a2" },
    { q: "faq.s4.q3", a: "faq.s4.a3" },
  ],
  5: [
    { q: "faq.s5.q1", a: "faq.s5.a1" },
    { q: "faq.s5.q2", a: "faq.s5.a2" },
    { q: "faq.s5.q3", a: "faq.s5.a3" },
  ],
  6: [
    { q: "faq.s6.q1", a: "faq.s6.a1" },
    { q: "faq.s6.q2", a: "faq.s6.a2" },
    { q: "faq.s6.q3", a: "faq.s6.a3" },
  ],
  7: [
    { q: "faq.s7.q1", a: "faq.s7.a1" },
    { q: "faq.s7.q2", a: "faq.s7.a2" },
    { q: "faq.s7.q3", a: "faq.s7.a3" },
  ],
  8: [
    { q: "faq.s8.q1", a: "faq.s8.a1" },
    { q: "faq.s8.q2", a: "faq.s8.a2" },
  ],
  9: [
    { q: "faq.s9.q1", a: "faq.s9.a1" },
    { q: "faq.s9.q2", a: "faq.s9.a2" },
  ],
};

export default function HelpOverlay({ onClose, fieldTag }: Props) {
  const { t, tArr } = useLang();
  const { currentStep, data } = useForm();

  // If a fieldTag is passed (from "Ask us" links), jump straight to custom question
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const [showCustom, setShowCustom] = useState(!!fieldTag);
  const [message, setMessage] = useState(fieldTag ? `[${fieldTag}] ` : "");
  const [sent, setSent] = useState(false);

  const stepNames = tArr("progress.steps");
  const currentSectionName = stepNames[currentStep - 1] ?? `Step ${currentStep}`;
  const faqStepMap: Record<number, number> = {
    1: 1,
    3: 4,
    4: 5,
  };
  const mergedEligibilityFaqs = [...(FAQ_BY_STEP[2] ?? []), ...(FAQ_BY_STEP[3] ?? [])];
  const faqs =
    currentStep === 2
      ? mergedEligibilityFaqs
      : FAQ_BY_STEP[faqStepMap[currentStep] ?? currentStep] ?? [];

  const handleToggle = (idx: number) => {
    setOpenIdx((prev) => (prev === idx ? null : idx));
  };

  const handleSend = () => {
    if (!message.trim()) return;
    setSent(true);
    setMessage("");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel — bottom sheet on mobile, centered modal on desktop */}
      <div className="relative w-full sm:max-w-[420px] sm:mx-4 bg-white rounded-t-2xl sm:rounded-2xl max-h-[75vh] overflow-hidden flex flex-col animate-[slideUp_0.25s_ease-out] sm:animate-[fadeScaleIn_0.2s_ease-out]">

        {/* Header */}
        <div className="flex items-start justify-between px-5 pt-5 pb-3 shrink-0">
          <div>
            <h2 className="text-[17px] font-semibold text-slate-900 leading-snug">
              {t("faq.title")}
            </h2>
            <p className="text-[12px] text-slate-400 mt-0.5">
              {t("faq.about")}{" "}
              <span className="text-slate-500 font-medium">{currentSectionName}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 cursor-pointer transition-colors shrink-0 mt-0.5 ml-3"
            aria-label="Close"
          >
            <i className="ri-close-line text-slate-500 text-lg" />
          </button>
        </div>

        {/* Drag handle (mobile only) */}
        <div className="flex justify-center pb-1 sm:hidden">
          <div className="w-8 h-1 rounded-full bg-slate-200" />
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1 px-5 pb-6">

          {/* FAQ Accordion */}
          {faqs.length > 0 && (
            <div className="divide-y divide-slate-100">
              {faqs.map((item, idx) => {
                const isOpen = openIdx === idx;
                return (
                  <div key={idx}>
                    <button
                      type="button"
                      onClick={() => handleToggle(idx)}
                      className="flex items-center justify-between w-full py-3.5 text-left cursor-pointer group"
                    >
                      <span className="text-[14px] font-medium text-slate-800 pr-3 leading-snug group-hover:text-emerald-700 transition-colors">
                        {t(item.q)}
                      </span>
                      <span className="w-5 h-5 flex items-center justify-center shrink-0">
                        <i
                          className={`ri-arrow-down-s-line text-slate-400 text-lg transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                        />
                      </span>
                    </button>

                    {/* Accordion answer — smooth grid-rows animation */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateRows: isOpen ? "1fr" : "0fr",
                        transition: "grid-template-rows 0.2s ease",
                      }}
                    >
                      <div style={{ overflow: "hidden" }}>
                        <div className="bg-slate-50 rounded-xl p-4 mb-3">
                          <p className="text-[14px] text-slate-600 leading-relaxed">
                            {t(item.a)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* "Other question" section — accordion row, same pattern as FAQ */}
          <div className="mt-4 border-t border-slate-100">
            {/* Tappable row — always visible */}
            <button
              type="button"
              onClick={() => { if (!sent) setShowCustom((prev) => !prev); }}
              className="flex items-center justify-between w-full py-3.5 text-left cursor-pointer group"
            >
              <span className="flex items-center gap-2 text-emerald-600 text-[14px] font-medium group-hover:text-emerald-700 transition-colors">
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-chat-1-line text-base" />
                </span>
                {t("faq.other")}
              </span>
              {!sent && (
                <span className="w-5 h-5 flex items-center justify-center shrink-0">
                  <i
                    className={`ri-arrow-down-s-line text-slate-400 text-lg transition-transform duration-200 ${showCustom ? "rotate-180" : ""}`}
                  />
                </span>
              )}
            </button>

            {/* Expandable content — accordion animation */}
            <div
              style={{
                display: "grid",
                gridTemplateRows: showCustom ? "1fr" : "0fr",
                transition: "grid-template-rows 0.2s ease",
              }}
            >
              <div style={{ overflow: "hidden" }}>
                <div className="pb-3">
                  {sent ? (
                    <div className="flex items-start gap-3 bg-emerald-50 rounded-xl p-4">
                      <span className="w-8 h-8 flex items-center justify-center rounded-full bg-emerald-100 shrink-0">
                        <i className="ri-check-line text-emerald-600 text-base" />
                      </span>
                      <p className="text-[14px] text-emerald-800 font-medium leading-snug pt-1">
                        {data.advisor
                          ? `${t("faq.sentTo")} ${data.advisor}. ${t("faq.sentContact")}`
                          : t("faq.sentTeam")}
                      </p>
                    </div>
                  ) : (
                    <>
                      <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder={t("faq.otherPlaceholder")}
                        rows={5}
                        className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                      <p className="text-[12px] text-slate-400 mt-1.5 mb-3">
                        {t("faq.otherHelper")}
                      </p>
                      <button
                        type="button"
                        onClick={handleSend}
                        disabled={!message.trim()}
                        className="w-full flex items-center justify-center gap-1.5 py-2.5 bg-emerald-600 text-white text-sm font-semibold rounded-xl cursor-pointer hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                      >
                        <i className="ri-send-plane-line text-sm" />
                        {t("faq.send")}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
